import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { CurrencyProvider } from "@/hooks/useCurrency";
import { ThemeProvider } from "next-themes";
import NavBar from "@/components/NavBar";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
const Index = lazy(() => import("./pages/Index"));
import { Loader2 } from "lucide-react";

const Results = lazy(() => import("./pages/Results"));
const DestinationDetail = lazy(() => import("./pages/DestinationDetail"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Favorites = lazy(() => import("./pages/Favorites"));
const SearchHistory = lazy(() => import("./pages/SearchHistory"));
const SharedComparison = lazy(() => import("./pages/SharedComparison"));
const Guides = lazy(() => import("./pages/Guides"));
const GuideDetail = lazy(() => import("./pages/GuideDetail"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SharedItinerary = lazy(() => import("./pages/SharedItinerary"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const PackingList = lazy(() => import("./pages/PackingList"));
const SharedBudget = lazy(() => import("./pages/SharedBudget"));
const TravelBingo = lazy(() => import("./pages/TravelBingo"));
const SharedBingo = lazy(() => import("./pages/SharedBingo"));
const TravelQuiz = lazy(() => import("./pages/TravelQuiz"));
const SharedQuizResult = lazy(() => import("./pages/SharedQuizResult"));
const Explore = lazy(() => import("./pages/Explore"));
const ExploreCategory = lazy(() => import("./pages/ExploreCategory"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Loading page">
    <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
    <span className="sr-only">Loading page…</span>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <AuthProvider>
      <CurrencyProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <div className="flex flex-col min-h-screen">
          <ScrollToTop />
          <NavBar />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/results" element={<Results />} />
            <Route path="/destination/:id" element={<DestinationDetail />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/history" element={<SearchHistory />} />
            <Route path="/compare/:shortCode" element={<SharedComparison />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guides/:slug" element={<GuideDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/itinerary/:shortCode" element={<SharedItinerary />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/packing-list" element={<PackingList />} />
            <Route path="/budget/:shortCode" element={<SharedBudget />} />
            <Route path="/travel-bingo" element={<TravelBingo />} />
            <Route path="/bingo/:shortCode" element={<SharedBingo />} />
            <Route path="/travel-quiz" element={<TravelQuiz />} />
            <Route path="/quiz/:shortCode" element={<SharedQuizResult />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/explore/:slug" element={<ExploreCategory />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <Footer />
          <ScrollToTopButton />
          </div>
        </BrowserRouter>
      </TooltipProvider>
      </CurrencyProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
